import { useState, useCallback, useMemo } from "react";
import { View, Text, ScrollView, Pressable, Platform } from "react-native";
import {
  Ban,
  ChevronLeft,
  CircleHelp,
  Layers,
  ShieldCheck,
  Sparkles,
  TrendingUp,
} from "lucide-react-native";
import type { LucideIcon } from "lucide-react-native";
import type { NativeStackScreenProps } from "@react-navigation/native-stack";
import { getBlocksByKind, type ContentBlock } from "@src/packages/content";
import { EMBER } from "../../../onboarding/theme";
import type { AccessStackParamList } from "../../types";
import { useSmartBack } from "../../hooks/useSmartBack";
import { MoonFilterChip, MoonSearchInput } from "@louise/moon-ui";
import GlassScreenLayout from "@louise/moon-ui/primitives/GlassScreenLayout";
import { SCREEN_PADDING_TOP } from "@louise/moon-ui/theme/tokens";
import TherapyCard from "../../components/access/TherapyCard";
import {
  useLocale,
  useTranslations,
  type TranslationFunction,
} from "@src/packages/i18n";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";

/* ─── Category display config ────────────────────────────────────── */

type EvidenceFilter =
  "all" | "strong" | "moderate" | "emerging" | "mixed" | "debunked";

function buildEvidenceFilters(t: TranslationFunction): {
  label: string;
  value: EvidenceFilter;
  icon: LucideIcon;
}[] {
  return [
    {
      label: t("access.therapies.evidence_filter.all"),
      value: "all",
      icon: Layers,
    },
    {
      label: t("access.therapies.evidence_filter.strong"),
      value: "strong",
      icon: ShieldCheck,
    },
    {
      label: t("access.therapies.evidence_filter.moderate"),
      value: "moderate",
      icon: TrendingUp,
    },
    {
      label: t("access.therapies.evidence_filter.emerging"),
      value: "emerging",
      icon: Sparkles,
    },
    {
      label: t("access.therapies.evidence_filter.mixed"),
      value: "mixed",
      icon: CircleHelp,
    },
    {
      label: t("access.therapies.evidence_filter.debunked"),
      value: "debunked",
      icon: Ban,
    },
  ];
}

const EVIDENCE_SORT_RANK: Record<string, number> = {
  strong: 0,
  moderate: 1,
  emerging: 2,
  mixed: 3,
  debunked: 4,
};

type TherapyCategory = ContentBlock<"therapy">["body"]["category"];

const THERAPY_CATEGORY_SORT_RANK: Record<TherapyCategory, number> = {
  clinical: 0,
  physical: 1,
  traditional: 2,
  "mind-body": 3,
  nutritional: 4,
  lifestyle: 5,
};

type Props = NativeStackScreenProps<AccessStackParamList, "Therapies">;

export default function TherapiesScreen({ navigation, route }: Props) {
  const t = useTranslations();
  const capturePress = usePressCapture("TherapiesScreen");
  const handleBack = useSmartBack({
    returnTab: route.params?.returnTab,
    fallbackTab: "Access",
    fallbackScreen: "AccessOverview",
  });
  const { locale } = useLocale();
  const [search, setSearch] = useState("");
  const [evidenceFilter, setEvidenceFilter] = useState<EvidenceFilter>("all");

  const therapies = useMemo(() => getBlocksByKind("therapy"), []);
  const evidenceFilters = useMemo(() => buildEvidenceFilters(t), [t]);

  const filtered = useMemo(() => {
    const bodyFor = (item: ContentBlock<"therapy">) =>
      locale === "fr" && item.bodyFr ? item.bodyFr : item.body;
    let result = [...therapies];

    if (search.trim()) {
      const q = search.trim().toLowerCase();
      result = result.filter((item) => {
        const body = bodyFor(item);
        return (
          body.name.toLowerCase().includes(q) ||
          item.title.toLowerCase().includes(q) ||
          body.description.toLowerCase().includes(q)
        );
      });
    }

    if (evidenceFilter !== "all") {
      result = result.filter(
        (item) => bodyFor(item).evidenceStrength === evidenceFilter,
      );
    }

    result.sort((a, b) => {
      const aBody = bodyFor(a);
      const bBody = bodyFor(b);
      const aCategoryRank = THERAPY_CATEGORY_SORT_RANK[aBody.category] ?? 99;
      const bCategoryRank = THERAPY_CATEGORY_SORT_RANK[bBody.category] ?? 99;
      if (aCategoryRank !== bCategoryRank) {
        return aCategoryRank - bCategoryRank;
      }

      const aRank = EVIDENCE_SORT_RANK[aBody.evidenceStrength] ?? 99;
      const bRank = EVIDENCE_SORT_RANK[bBody.evidenceStrength] ?? 99;
      if (aRank !== bRank) return aRank - bRank;
      return aBody.name.localeCompare(bBody.name);
    });

    return result;
  }, [therapies, search, evidenceFilter, locale]);

  const onItemPress = useCallback(
    (item: ContentBlock<"therapy">) => {
      navigation.navigate("TherapyDetail", { therapyId: item.id });
    },
    [navigation],
  );

  // Pair items into rows of 2
  const rows = useMemo(() => {
    const result: ContentBlock<"therapy">[][] = [];
    for (let i = 0; i < filtered.length; i += 2) {
      result.push(filtered.slice(i, i + 2));
    }
    return result;
  }, [filtered]);

  return (
    <GlassScreenLayout>
      <ScrollView
        contentContainerStyle={{
          paddingHorizontal: 16,
          paddingTop: SCREEN_PADDING_TOP,
          paddingBottom: 100,
          maxWidth: 520,
          width: "100%",
          alignSelf: "center",
        }}
        showsVerticalScrollIndicator={false}
      >
        {/* Back button */}
        <Pressable
          onPress={() => {
            capturePress(
              pressLabel(
                route.params?.returnTab
                  ? t("you.shared.back")
                  : t("access.back"),
              ),
              "back",
            );
            handleBack();
          }}
          style={{
            flexDirection: "row",
            alignItems: "center",
            gap: 4,
            marginBottom: 8,
          }}
        >
          <ChevronLeft size={18} color={EMBER.colors.warm} />
          <Text
            className="font-dm-sans"
            style={{ fontSize: 14, color: EMBER.colors.warm }}
          >
            {route.params?.returnTab ? t("you.shared.back") : t("access.back")}
          </Text>
        </Pressable>

        {/* Title */}
        <Text
          className="font-dm-sans"
          style={
            {
              fontSize: 38,
              fontWeight: "700",
              color: "#3B1A08",
              lineHeight: 42,
              letterSpacing: -0.5,
              ...(Platform.OS === "web" && {
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.7' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='200' height='200' filter='url(%23n)' opacity='0.06'/%3E%3C/svg%3E"), linear-gradient(135deg, #3B1A08 0%, #7A3E15 35%, #3B1A08 60%, #6B3410 85%, #3B1A08 100%)`,
                backgroundSize: "200px 200px, 100% 100%",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }),
            } as Record<string, unknown>
          }
        >
          {t("access.therapies.title")}
        </Text>
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 15,
            color: EMBER.colors.textLight,
            marginTop: 2,
            lineHeight: 21,
          }}
        >
          {t("access.therapies.subtitle")}
        </Text>

        <MoonSearchInput
          placeholder={t("access.therapies.search_placeholder")}
          value={search}
          onChangeText={setSearch}
          onClear={() => setSearch("")}
          tone="glass"
          style={{ marginTop: 18 }}
        />

        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={{
            gap: 8,
            paddingRight: 4,
          }}
          style={{
            marginTop: 12,
            marginHorizontal: -2,
            paddingHorizontal: 2,
          }}
        >
          {evidenceFilters.map((filter) => (
            <MoonFilterChip
              key={filter.value}
              label={filter.label}
              icon={filter.icon}
              selected={evidenceFilter === filter.value}
              onPress={() => setEvidenceFilter(filter.value)}
              tone="glass"
              showCheck={false}
            />
          ))}
        </ScrollView>

        {/* 2x2 Grid */}
        <View style={{ gap: 12, marginTop: 16 }}>
          {rows.map((row, rowIdx) => (
            <View key={rowIdx} style={{ flexDirection: "row", gap: 12 }}>
              {row.map((therapy) => (
                <TherapyCard
                  key={therapy.id}
                  therapy={therapy}
                  onPress={onItemPress}
                />
              ))}
              {row.length === 1 && <View style={{ flex: 1 }} />}
            </View>
          ))}
        </View>

        {/* Empty state */}
        {filtered.length === 0 && (
          <View style={{ alignItems: "center", paddingTop: 40 }}>
            <Text
              className="font-dm-sans"
              style={{
                fontSize: 15,
                color: EMBER.colors.textLight,
                textAlign: "center",
              }}
            >
              {t("access.therapies.empty")}
            </Text>
          </View>
        )}
      </ScrollView>
    </GlassScreenLayout>
  );
}
